import { Search, SettingsOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { NavLink, useNavigate } from 'react-router-dom';

import { useToggleState } from '@/hooks/useToggleState';
import { trackerUrls } from '@/tracker/config/urls';
import { BoardSettingsDialog } from '@/tracker/features/boardSettings';
import { useTrackerStore } from '@/tracker/model';
import { Board } from '@/tracker/types';
import { FavoriteButton } from '@/tracker/ui/FavoriteButton';
import { PageNav } from '@/tracker/ui/PageNav';

import { BoardTabLabel } from '../BoardTabLabel';

import s from './BoardHeader.module.scss';

type Props = {
  board: Board;
  /** Поиск задач вкладки по ключу и названию */
  query: string;
  /** Настройки — владельцу доски и админу, как на бэке */
  canManage: boolean;
  onQueryChange: (query: string) => void;
  /** Доску изменили в настройках */
  onBoardChange: (board: Board) => void;
};

/** Шапка доски как в Яндекс Трекере: поиск задач, избранное и вкладки «Бэклог» / «Доска» */
const BoardHeader: React.FC<Props> = observer(
  ({ board, query, canManage, onQueryChange, onBoardChange }) => {
    const trackerStore = useTrackerStore();
    const navigate = useNavigate();
    const settings = useToggleState();

    return (
      <div className={s.header}>
        <PageNav parent={{ label: 'Доски', to: trackerUrls.boards }} current={board.name} />
        <div className={s.header__row}>
          <TextField
            size="small"
            sx={{ width: 320 }}
            placeholder="Поиск по названию или ключу"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': 'Поиск задач на доске' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FavoriteButton
            isFavorite={trackerStore.isFavoriteBoard(board.id)}
            onToggle={() => trackerStore.toggleFavoriteBoard(board)}
          />
          {canManage && (
            <Tooltip title="Настройки доски">
              <IconButton size="small" aria-label="Настройки доски" onClick={settings.open}>
                <SettingsOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <nav className={s.tabs} aria-label="Вид доски">
            <NavLink
              to={trackerUrls.boardBacklog(board.id)}
              className={({ isActive }) => cn(s.tabs__tab, isActive && s.tabs__tab_active)}
            >
              {({ isActive }) => <BoardTabLabel text="Бэклог" isActive={isActive} />}
            </NavLink>
            {/* end: путь доски — префикс пути бэклога */}
            <NavLink
              to={trackerUrls.board(board.id)}
              end
              className={({ isActive }) => cn(s.tabs__tab, isActive && s.tabs__tab_active)}
            >
              {({ isActive }) => <BoardTabLabel text="Доска" isActive={isActive} />}
            </NavLink>
          </nav>
        </div>

        <AnimatePresence>
          {settings.opened && (
            <BoardSettingsDialog
              board={board}
              onSaved={(saved) => {
                onBoardChange(saved);
                settings.close();
              }}
              onDeleted={() => {
                trackerStore.forgetBoard(board.id);
                navigate(trackerUrls.boards);
              }}
              onClose={settings.close}
            />
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export { BoardHeader };
